import { CheckboxField } from "./CheckboxField";
import {
    DEFAULT_FILL_COLOR,
    solidFill,
} from "../../utils/svgDiagram2DModel";
import type { DiagramPattern, Fill } from "../../utils/svgDiagram2DModel";

interface FillControlProps {
    /** Current fill (null = no fill). */
    fill: Fill | null;
    /** Document-level patterns available to pick from for a pattern fill. */
    patterns: DiagramPattern[];
    /** Called with the next fill (or null to clear it). */
    onChange: (fill: Fill | null) => void;
    /** Accessible label fragment, e.g. "Rectangle" → "Rectangle fill color". */
    label?: string;
}

/**
 * Inline fill editor shared by every fillable shape/group inspector: a "Fill"
 * checkbox, a Solid/Pattern mode select, and either a color swatch (solid) or a
 * pattern dropdown (pattern). Patterns come from the document-level pattern
 * library; when none exist, the Pattern option is disabled with a hint.
 */
export function FillControl({
    fill,
    patterns,
    onChange,
    label = "Shape",
}: FillControlProps) {
    const hasPatterns = patterns.length > 0;

    /** First pattern fill we can fall back to when switching to Pattern mode. */
    const firstPatternFill = (): Fill | null =>
        hasPatterns ? { type: "pattern", patternId: patterns[0]!.id } : null;

    return (
        <div
            style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                alignItems: "center",
            }}
        >
            <CheckboxField
                checked={fill != null}
                onChange={(checked) =>
                    onChange(checked ? solidFill(DEFAULT_FILL_COLOR) : null)
                }
                label="Fill"
                inline
            />

            {fill != null && (
                <select
                    aria-label={`${label} fill type`}
                    value={fill.type}
                    onChange={(e) => {
                        if (e.target.value === "pattern") {
                            const pf = firstPatternFill();
                            if (pf) onChange(pf);
                        } else {
                            onChange(solidFill(DEFAULT_FILL_COLOR));
                        }
                    }}
                    style={{ width: "auto" }}
                >
                    <option value="solid">Solid</option>
                    <option value="pattern" disabled={!hasPatterns}>
                        Pattern{hasPatterns ? "" : " (none defined)"}
                    </option>
                </select>
            )}

            {fill?.type === "solid" && (
                <input
                    type="color"
                    aria-label={`${label} fill color`}
                    value={fill.color}
                    onChange={(e) => onChange(solidFill(e.target.value))}
                />
            )}

            {fill?.type === "pattern" && (
                <select
                    aria-label={`${label} fill pattern`}
                    value={fill.patternId}
                    onChange={(e) =>
                        onChange({ type: "pattern", patternId: e.target.value })
                    }
                    style={{ flex: 1, minWidth: 0 }}
                >
                    {patterns.map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.name}
                        </option>
                    ))}
                </select>
            )}
        </div>
    );
}
